import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18n from '@/i18n';
import {
  courseAppearanceService,
  type PageSection,
} from '@/services/course-appearance.service';
import { appearanceKeys } from './queryKeys';

/** Reorder sections optimistically (drag and drop). */
export function useReorderPageSections(productId: string) {
  const qc = useQueryClient();
  const key = appearanceKeys.sections(productId);

  return useMutation({
    mutationFn: (ids: string[]) =>
      courseAppearanceService.reorderPageSections(productId, ids),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<PageSection[]>(key);
      if (prev) {
        const byId = new Map(prev.map((s) => [s.id, s]));
        const next = ids
          .map((id, i) => {
            const s = byId.get(id);
            return s ? { ...s, order: i } : undefined;
          })
          .filter((s): s is PageSection => !!s);
        qc.setQueryData<PageSection[]>(key, next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error(i18n.t('adminAppearance:toasts.reorderSectionsError'));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
